/**
 * Redstone Relay (Fun section). The board is plain buttons (tap a piece, tap a cell), so it works
 * with touch, mouse, keyboard and screen readers. The browser plays the run back tick by tick;
 * the server re-runs the same engine to award medals and rewards.
 */
import { LitElement, css, html, nothing, svg } from 'lit';
import {
  DIRECTIONAL,
  DX,
  DY,
  REDSTONE_LEVELS,
  applyTap,
  cellSize,
  medalOf,
  nextLevelId,
  rewardById,
  run,
  type Board,
  type Dir,
  type FixedCell,
  type Medal,
  type PieceKind,
  type Placement,
  type RedstoneLevel,
  type RunResult,
  type Tool,
  type WrongLamp,
  wrongLamps,
} from '@ka/fun';
import { markWalkthroughSeen, shared, walkthroughSeen, type KaWalkthrough } from '@ka/ui';
import { currentFun, invitation, itemIcon, loadFun, setFun, type FunView } from './fun.ts';
import { currentKid, kidApi } from './kid.ts';
import { WALK_LEVEL, redstoneSteps, walkBoardDone, walkTapOk } from './redstone-walkthrough.ts';
import { navigate } from './router.ts';

const PIECE_LABEL: Record<PieceKind, string> = {
  wire: 'Wire',
  repeater: 'Repeater',
  not: 'Torch',
  and: 'Both',
  or: 'Either',
};
const SHORT: Record<Exclude<PieceKind, 'wire'>, string> = {
  repeater: 'R',
  not: 'T',
  and: 'B',
  or: 'E',
};
const DIR_NAME = ['up', 'right', 'down', 'left'];
const markText = (m: WrongLamp) =>
  m.want === 'lit' ? 'This lamp is still dark.' : 'This lamp should stay dark!';
const draftKey = (l: RedstoneLevel) => `ka.fun.redstone.${l.id}.v${l.version}`;

function loadDraft(l: RedstoneLevel): Board {
  try {
    const b = JSON.parse(localStorage.getItem(draftKey(l)) ?? 'null') as Board | null;
    if (b && Array.isArray(b.placements) && b.levers && typeof b.levers === 'object') return b;
  } catch {
    /* no draft */
  }
  return { placements: [], levers: {} };
}
function saveDraft(l: RedstoneLevel, b: Board) {
  try {
    localStorage.setItem(draftKey(l), JSON.stringify(b));
  } catch {
    /* private mode: the board still works, it just is not kept */
  }
}

export class KaRedstone extends LitElement {
  static override properties = {
    levelId: {},
    board: { state: true },
    tool: { state: true },
    result: { state: true },
    tick: { state: true },
    hints: { state: true },
    server: { state: true },
    view: { state: true },
    cell: { state: true },
    sheetOpen: { state: true },
    marks: { state: true },
    walk: { state: true },
    walkStep: { state: true },
  };
  declare levelId: string;
  declare board: Board;
  declare tool: Tool;
  declare result: RunResult | null;
  declare tick: number;
  declare hints: number;
  declare server: {
    state: 'idle' | 'saving' | 'saved' | 'error';
    medal?: Medal | null;
    newRewards?: string[];
    message?: string;
  };
  declare view: FunView | null;
  /** Board square size in px (44..64), measured from the board's box. */
  declare cell: number;
  /** The result sheet is up; `marks` are the wrong lamps of the last run (kept until the next edit). */
  declare private sheetOpen: boolean;
  declare private marks: WrongLamp[];
  /** The walkthrough is running on level 1, on a board that is not saved as the kid's draft. */
  declare private walk: boolean;
  declare private walkStep: string | null;
  private walkNext = false;
  private walkChecked = false;
  private steps = redstoneSteps({
    tile: () => this.renderRoot.querySelector('.tile'),
    firstCell: () => this.renderRoot.querySelectorAll('.cell')[6], // (1,1) on the 5-wide board
    board: () => this.renderRoot.querySelector('.board'),
    run: () => this.renderRoot.querySelector('.run'),
    result: () =>
      this.renderRoot.querySelector('.sheet') ?? this.renderRoot.querySelector('.board'),
  });
  private undoStack: Board[] = [];
  private timer?: ReturnType<typeof setInterval>;

  static override styles = [
    shared,
    css`
      :host {
        display: grid;
        gap: 1rem;
      }
      .row {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
        align-items: center;
      }
      .board {
        display: grid;
        gap: 2px;
        background: var(--ka-edge);
        border: 3px solid var(--ka-edge);
        width: fit-content;
        touch-action: manipulation;
      }
      .cell {
        all: unset;
        box-sizing: border-box;
        width: var(--cell);
        aspect-ratio: 1;
        background: var(--ka-stone-700);
        display: grid;
        place-items: center;
        cursor: pointer;
        position: relative;
      }
      .cell:focus-visible {
        outline: 3px solid var(--ka-focus);
        z-index: 1;
      }
      .cell.block {
        background: repeating-linear-gradient(45deg, #2a2a30 0 4px, #1c1c22 4px 8px);
        cursor: default;
      }
      .cell svg {
        width: 100%;
        height: 100%;
      }
      .btn[aria-pressed='true'] {
        background: var(--ka-gold);
        color: var(--ka-ink-on-gold);
      }
      .count {
        font-size: 0.8rem;
        opacity: 0.85;
      }
      .levels {
        list-style: none;
        padding: 0;
        margin: 0;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(min(100%, 9rem), 1fr));
        gap: 0.5rem;
      }
      .levels a {
        display: grid;
        gap: 0.25rem;
        align-content: start;
        padding: 0.75rem;
        min-height: 44px;
        height: 100%;
        box-sizing: border-box;
        background: var(--ka-stone-800);
        border: 2px solid var(--ka-edge);
        color: var(--ka-parchment);
        text-decoration: none;
      }
      .removing {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 0.5rem;
        margin: 0;
      }
      .levels a.next {
        outline: 3px solid var(--ka-focus);
      }
      .num {
        font: 600 1.5rem var(--ka-font-display);
      }
      .medal {
        font-family: var(--ka-font-display);
        color: var(--ka-gold);
      }
      .cell.marked {
        outline: 3px solid var(--ka-focus);
        z-index: 2;
      }
      .callout {
        position: absolute;
        top: calc(100% + 4px);
        left: 0;
        padding: 0.2em 0.5em;
        background: var(--ka-focus);
        color: var(--ka-ink-on-gold);
        font: 600 0.8rem var(--ka-font-display);
        white-space: nowrap;
        pointer-events: none;
      }
      .callout.up {
        top: auto;
        bottom: calc(100% + 4px);
      }
      .callout.end {
        left: auto;
        right: 0;
      }
      .sheet h2,
      .sheet p {
        margin: 0;
      }
      .small {
        font-size: 0.9rem;
      }
      .top {
        display: flex;
        flex-wrap: wrap;
        gap: 0.25rem 1rem;
        align-items: center;
        margin: 0;
      }
      .top a {
        min-height: 44px;
        display: inline-flex;
        align-items: center;
      }
      .title {
        font: 600 1.25rem var(--ka-font-display);
      }
      .play,
      .side,
      .board-col {
        display: grid;
        gap: 1rem;
        align-content: start;
        min-width: 0;
      }
      .goal p:last-child {
        margin-bottom: 0;
      }
      .board-box {
        overflow-x: auto;
        min-width: 0;
      }
      .board-col > p {
        margin: 0;
      }
      .dock,
      .sheet {
        display: grid;
        gap: 0.5rem;
      }
      .tray {
        display: flex;
        flex-wrap: wrap;
        gap: 0.5rem;
      }
      .tile {
        padding: 0.4em 0.8em;
      }
      .tile svg {
        width: 28px;
        height: 28px;
        flex: none;
      }
      .help {
        margin: 0;
      }
      .bar {
        display: flex;
        gap: 0.5rem;
        align-items: center;
      }
      .run {
        flex: 1;
        justify-content: center;
      }
      .more {
        position: relative;
      }
      .more summary {
        list-style: none;
        box-sizing: border-box;
      }
      .more summary::-webkit-details-marker {
        display: none;
      }
      .more-body {
        position: absolute;
        right: 0;
        bottom: calc(100% + 0.5rem);
        z-index: 5;
        box-sizing: border-box;
        width: min(22rem, calc(100vw - 2rem));
        display: grid;
        gap: 0.75rem;
        box-shadow: var(--ka-lift);
      }
      .more-body p {
        margin: 0;
      }
      .more-body summary {
        min-height: 44px;
        display: flex;
        align-items: center;
        cursor: pointer;
      }
      /* Phones: the tray and the bar (or the result sheet) are pinned to the bottom. */
      @media (max-width: 899px) {
        .side {
          display: contents;
        }
        .dock,
        .sheet {
          position: fixed;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 4;
          padding: 0.75rem 1rem 1rem;
          background: var(--ka-stone-800);
          border-top: 3px solid var(--ka-edge);
          box-shadow: var(--ka-bevel);
        }
        .tray {
          flex-wrap: nowrap;
          overflow-x: auto;
          padding: 4px;
        }
        .tile {
          flex: none;
        }
        /* 3–4 piece types: two columns, so every tile shows without scrolling. */
        .tray.many {
          display: grid;
          grid-template-columns: 1fr 1fr;
          overflow: visible;
        }
        .tray.many .tile {
          min-width: 0;
          justify-content: flex-start;
        }
        .sheet {
          z-index: 5;
        }
        .sheet + .dock {
          display: none;
        }
      }
      @media (min-width: 900px) {
        .play {
          grid-template-columns: auto minmax(16rem, 22rem);
          align-items: start;
        }
        .side {
          grid-column: 2;
        }
        .board-col {
          grid-column: 1;
          grid-row: 1;
        }
        .more-body {
          bottom: auto;
          top: calc(100% + 0.5rem);
        }
      }
    `,
  ];

  constructor() {
    super();
    this.levelId = '';
    this.board = { placements: [], levers: {} };
    this.tool = 'wire';
    this.cell = 44;
    this.sheetOpen = false;
    this.marks = [];
    this.walk = false;
    this.walkStep = null;
    this.result = null;
    this.tick = 0;
    this.hints = 0;
    this.server = { state: 'idle' };
    this.view = currentFun();
  }

  private get level() {
    return REDSTONE_LEVELS.find((l) => l.id === this.levelId);
  }

  override connectedCallback() {
    super.connectedCallback();
    addEventListener('resize', this.measure);
    if (currentKid() && !this.view)
      void loadFun()
        .then((f) => (this.view = f))
        .catch(() => undefined);
  }
  override disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this.timer);
    removeEventListener('resize', this.measure);
    this.style.paddingBottom = '';
  }
  override updated() {
    this.measure();
    // First time on this device: the walkthrough runs from the list or level 1.
    if (!this.walkChecked && currentKid()) {
      this.walkChecked = true;
      if (!walkthroughSeen('redstone') && (!this.levelId || this.levelId === WALK_LEVEL))
        this.startWalk();
    }
  }

  /** Square size from the board's box; under 900px, room at the bottom for the pinned tray and bar. */
  private measure = () => {
    const box = this.renderRoot.querySelector<HTMLElement>('.board-box');
    // The sheet comes first in the column, and on phones it hides the dock.
    const dock = this.renderRoot.querySelector<HTMLElement>('.sheet, .dock');
    const l = this.level;
    this.style.paddingBottom =
      dock && getComputedStyle(dock).position === 'fixed' ? `${dock.offsetHeight}px` : '';
    if (!box || !l) return;
    // The frame: a 3px border each side and 2px between squares.
    const size = cellSize(l.width, box.clientWidth - 6 - 2 * (l.width - 1));
    if (size !== this.cell) this.cell = size;
  };

  override willUpdate(changed: Map<string, unknown>) {
    if (changed.has('levelId')) this.clearResult();
    if (changed.has('levelId') && this.level) {
      this.board = loadDraft(this.level);
      this.undoStack = [];
      this.hints = 0;
      const firstPiece = Object.keys(this.level.supply)[0] as PieceKind | undefined;
      this.tool = firstPiece ?? 'remove';
    }
    // Leaving level 1 stops the walkthrough; arriving there from How to play starts it.
    if (changed.has('levelId')) {
      this.walk = false;
      if (this.walkNext && this.levelId === WALK_LEVEL) this.beginWalk();
      this.walkNext = false;
    }
  }

  // ---------- editing ----------

  private edit(next: Board) {
    if (!this.level) return;
    this.undoStack = [...this.undoStack.slice(-49), this.board];
    this.board = next;
    this.clearResult();
    if (!this.walk) saveDraft(this.level, next);
  }

  private remaining(piece: PieceKind) {
    return (
      (this.level?.supply[piece] ?? 0) -
      this.board.placements.filter((p) => p.piece === piece).length
    );
  }

  private tap(x: number, y: number) {
    if (this.walk && !walkTapOk(this.walkStep, x, y)) return;
    const next = applyTap(this.level!, this.board, x, y, this.tool);
    if (next) this.edit(next);
    if (this.walk && walkBoardDone(this.walkStep, this.board)) this.coach?.complete(this.walkStep!);
  }

  private pick(p: PieceKind) {
    if (this.walk && this.walkStep !== 'wire') return;
    this.tool = p;
    if (this.walk) this.coach?.complete('wire');
  }

  private clearResult() {
    clearInterval(this.timer);
    this.result = null;
    this.marks = [];
    this.sheetOpen = false;
    this.server = { state: 'idle' };
  }

  /** Closes the sheet (marks stay) and returns focus to the board. */
  private closeSheet() {
    this.sheetOpen = false;
    void this.updateComplete.then(() =>
      this.renderRoot.querySelector<HTMLElement>('.cell')?.focus(),
    );
  }

  private toggleRemove() {
    const first = Object.keys(this.level?.supply ?? {})[0] as PieceKind | undefined;
    this.tool = this.tool === 'remove' ? (first ?? 'remove') : 'remove';
    this.closeMenu();
  }

  private reset() {
    this.closeMenu();
    if (confirm('Clear the board?')) this.edit({ placements: [], levers: {} });
  }

  private closeMenu() {
    const m = this.renderRoot.querySelector<HTMLDetailsElement>('details.more');
    if (m) m.open = false;
  }

  private undo() {
    if (this.walk) return;
    const prev = this.undoStack.pop();
    if (!prev || !this.level) return;
    this.board = prev;
    this.clearResult();
    saveDraft(this.level, prev);
    this.requestUpdate();
  }

  // ---------- running ----------

  private async runIt() {
    if (this.walk && this.walkStep !== 'run') return;
    const l = this.level!;
    const r = run(l, this.board);
    if (!r.ok) return;
    if (this.walk) this.coach?.complete('run');
    this.result = r;
    this.watch();
    if (r.outcome !== 'win' || !currentKid()) return;
    this.server = { state: 'saving' };
    try {
      const s = await kidApi<{ medal: Medal | null; newRewards: string[]; fun: FunView }>(
        'POST',
        `/fun/redstone/${l.id}`,
        { version: l.version, board: this.board },
      );
      setFun(s.fun);
      this.view = s.fun;
      this.server = { state: 'saved', medal: s.medal, newRewards: s.newRewards };
    } catch (e) {
      this.server = {
        state: 'error',
        message:
          e instanceof Error ? e.message : 'Could not save. Your board is kept on this device.',
      };
    }
  }

  /** Plays the run from tick 0 (the last tick at once under reduced motion), then opens the sheet. */
  private watch() {
    const r = this.result;
    if (!r) return;
    clearInterval(this.timer);
    this.sheetOpen = false;
    this.marks = [];
    const last = r.ticks.length - 1;
    this.tick = matchMedia('(prefers-reduced-motion: reduce)').matches ? last : 0;
    if (this.tick >= last) return this.showResult();
    this.timer = setInterval(() => {
      if (++this.tick < last) return;
      clearInterval(this.timer);
      this.showResult();
    }, 250);
  }

  private showResult() {
    if (!this.level) return;
    const r = this.result!;
    this.marks = r.outcome === 'wrong-lamps' ? wrongLamps(this.level!, r.ticks.at(-1)!) : [];
    this.sheetOpen = true;
    void this.updateComplete.then(() =>
      this.renderRoot.querySelector<HTMLElement>('.sheet h2')?.focus(),
    );
  }

  // ---------- drawing ----------

  private cellSvg(
    x: number,
    y: number,
    fixed: FixedCell | undefined,
    placed: Placement | undefined,
  ) {
    const t = this.result?.ticks[this.tick];
    const on = !!t?.powered.includes(`${x},${y}`);
    const lit = !!t?.lit.includes(`${x},${y}`);
    const kind =
      fixed?.kind === 'piece'
        ? 'wire'
        : (fixed?.kind ?? (placed?.piece === 'wire' ? 'wire' : placed ? 'gate' : 'empty'));
    if (kind === 'source')
      return svg`<svg viewBox="0 0 10 10"><rect x="2" y="2" width="6" height="6" fill="var(--ka-red-bright)" stroke="#000" stroke-width=".5"/><rect x="4" y="4" width="2" height="2" fill="#fff"/></svg>`;
    if (kind === 'lever') {
      const up = this.board.levers[(fixed as { id: string }).id];
      return svg`<svg viewBox="0 0 10 10"><rect x="2" y="6" width="6" height="2.5" fill="#777"/><rect x=${up ? 4.5 : 4.5} y=${up ? 1.5 : 4} width="1" height=${up ? 4.5 : 2} fill="#caa472" transform=${up ? '' : 'rotate(60 5 6)'}/><text x="5" y="9.8" font-size="1.8" text-anchor="middle" fill="#fff">${up ? 'ON' : 'OFF'}</text></svg>`;
    }
    if (kind === 'lamp') {
      const goal = (fixed as { goal: boolean }).goal;
      return svg`<svg viewBox="0 0 10 10">
        <circle cx="5" cy="5" r="3.4" fill=${lit ? (goal ? 'var(--ka-gold)' : 'var(--ka-red-bright)') : '#222'} stroke=${goal ? 'var(--ka-gold)' : 'var(--ka-red)'} stroke-width=".8"/>
        ${goal ? svg`<path d="M3.6 5.1 L4.7 6.2 L6.6 3.9" stroke=${lit ? '#000' : 'var(--ka-gold)'} stroke-width=".7" fill="none"/>` : svg`<path d="M3.7 3.7 L6.3 6.3 M6.3 3.7 L3.7 6.3" stroke=${lit ? '#000' : 'var(--ka-red)'} stroke-width=".7"/>`}
        ${lit ? svg`<path d="M5 .3 V1.3 M5 8.7 V9.7 M.3 5 H1.3 M8.7 5 H9.7" stroke="var(--ka-gold)" stroke-width=".6"/>` : nothing}
      </svg>`;
    }
    if (kind === 'wire')
      return this.glyph(
        'wire',
        1,
        on,
        [0, 1, 2, 3].filter((d) => this.connects(x + DX[d]!, y + DY[d]!)),
      );
    if (kind === 'gate' && placed) return this.glyph(placed.piece, placed.dir, on);
    return nothing;
  }

  /** A piece's picture, on the board and in the tray. Unpowered red is 3:1+ on --ka-stone-700. */
  private glyph(piece: PieceKind, dir: Dir, on: boolean, arms: number[] = [1, 3]) {
    const red = 'var(--ka-red-bright)';
    const dim = on ? 1 : 0.85;
    if (piece === 'wire') {
      const w = on ? 1.8 : 1.2; // 1 unit is at least 4.4px on a 44px square
      return svg`<svg viewBox="0 0 10 10" opacity=${dim}>
        ${arms.map((d) => svg`<line x1="5" y1="5" x2=${5 + DX[d]! * 5} y2=${5 + DY[d]! * 5} stroke=${red} stroke-width=${w}/>`)}
        <rect x=${5 - w} y=${5 - w} width=${w * 2} height=${w * 2} fill=${red}/>
        ${on ? svg`<rect x="4.5" y="4.5" width="1" height="1" fill="#fff"/>` : nothing}
      </svg>`;
    }
    const gate = piece as Exclude<PieceKind, 'wire'>;
    return svg`<svg viewBox="0 0 10 10">
      <rect x="1" y="1" width="8" height="8" fill="#5a5a62" stroke="#000" stroke-width=".4"/>
      <g transform="rotate(${dir * 90} 5 5)" opacity=${dim}><path d="M5 1.2 L7 3.6 H3 Z" fill=${red}/></g>
      <text x="5" y="7.6" font-size=${gate === 'repeater' ? 3 : 2.3} text-anchor="middle" fill="#fff" font-family="monospace">${SHORT[gate]}</text>
    </svg>`;
  }

  private connects(x: number, y: number) {
    const l = this.level!;
    const f = l.fixed[`${x},${y}`];
    if (f) return f.kind !== 'block';
    return this.board.placements.some((p) => p.x === x && p.y === y);
  }

  private cellLabel(
    x: number,
    y: number,
    fixed: FixedCell | undefined,
    placed: Placement | undefined,
  ) {
    const t = this.result?.ticks[this.tick];
    const on = t?.powered.includes(`${x},${y}`) ? ', powered' : '';
    const where = `Row ${y + 1}, column ${x + 1}: `;
    if (fixed?.kind === 'source') return where + 'power source';
    if (fixed?.kind === 'lever')
      return where + `lever, ${this.board.levers[fixed.id] ? 'on' : 'off'}. Tap to switch`;
    if (fixed?.kind === 'lamp')
      return (
        where +
        `${fixed.goal ? 'gold lamp (light it)' : 'red lamp (keep dark)'}${t?.lit.includes(`${x},${y}`) ? ', lit' : ', dark'}`
      );
    if (fixed?.kind === 'block') return where + 'stone';
    if (fixed?.kind === 'piece') return where + `fixed wire${on}`;
    if (placed)
      return (
        where +
        `${PIECE_LABEL[placed.piece]}${DIRECTIONAL.has(placed.piece) ? ` pointing ${DIR_NAME[placed.dir]}` : ''}${on}`
      );
    return where + 'empty';
  }

  // ---------- views ----------

  private levelList() {
    const solved = this.view?.progress.solved.redstone ?? {};
    const next = nextLevelId(REDSTONE_LEVELS, solved);
    const done = REDSTONE_LEVELS.filter((l) => solved[l.id]).length;
    const gold = REDSTONE_LEVELS.filter((l) => solved[l.id] === 'gold').length;
    return html`<p class="top"><a href="/fun">← Fun</a></p>
      <section class="panel">
        <h1>Redstone Relay</h1>
        <p>Get power to the ✓ lamps. Keep the ✕ lamps dark.</p>
      </section>
      <ol class="levels">
        ${REDSTONE_LEVELS.map(
          (l, i) =>
            html`<li>
              <a class=${l.id === next ? 'next' : ''} href="/fun/redstone?level=${l.id}">
                <span class="num">${i + 1}</span>
                <span>${l.title}</span>
                <span class=${l.id === next || solved[l.id] ? 'medal' : 'muted'}
                  >${
                    l.id === next
                      ? 'Play next →'
                      : solved[l.id] === 'gold'
                        ? '★ Gold'
                        : solved[l.id]
                          ? '✓ Solved'
                          : 'New'
                  }</span
                >
              </a>
            </li>`,
        )}
      </ol>
      <p class="row">
        <button class="btn" @click=${this.startWalk}>How to play</button>
        <span class="small muted">${done} / ${REDSTONE_LEVELS.length} · ★ ${gold}</span>
      </p>`;
  }

  private play(l: RedstoneLevel) {
    const idx = REDSTONE_LEVELS.indexOf(l);
    const next = REDSTONE_LEVELS[idx + 1];
    const r = this.result;
    const pieces = Object.keys(l.supply) as PieceKind[];
    const fixed = Object.values(l.fixed);
    const has = (k: FixedCell['kind']) => fixed.some((f) => f.kind === k);
    const lamps = fixed.filter((f) => f.kind === 'lamp');
    const key = [
      has('source') && '■ power',
      lamps.some((f) => f.goal) && '✓ light it',
      lamps.some((f) => !f.goal) && '✕ keep dark',
      has('block') && "▨ can't build",
      has('lever') && 'lever: tap to switch',
    ].filter(Boolean);
    return html`<p class="top">
        <a href="/fun/redstone">← Puzzles</a>
        <span class="title">${idx + 1} · ${l.title}</span>
      </p>
      <div class="play">
        <div class="side">
          <section class="panel goal">
            <p>${l.goal}</p>
            <p class="small muted">
              Gold medal: ${l.budget} pieces or fewer · ${this.board.placements.length} used
            </p>
            ${l.hints.slice(0, this.hints).map((h, i) => html`<p class="small">Hint ${i + 1}: ${h}</p>`)}
          </section>
          ${this.sheetOpen && r ? this.sheet(l, r, next) : nothing}
          <div class="dock">
            <div class="tray ${pieces.length > 2 ? 'many' : ''}" role="group" aria-label="Pieces">
              ${pieces.map((p) => {
                const left = this.remaining(p);
                return html`<button
                  class="btn tile"
                  aria-pressed=${this.tool === p}
                  ?disabled=${left <= 0}
                  @click=${() => this.pick(p)}
                >
                  ${this.glyph(p, 1, false)} ${PIECE_LABEL[p]}
                  <span class="count">×${left}</span>
                </button>`;
              })}
            </div>
            ${
              this.tool === 'remove'
                ? html`<p class="removing small">
                    Removing: tap a piece to take it off.
                    <button class="btn" @click=${this.toggleRemove}>Done</button>
                  </p>`
                : html`<p class="small muted help">
                    ${
                      pieces.length === 1 && pieces[0] === 'wire'
                        ? 'Tap a square to place wire. Tap it again to remove it.'
                        : 'Tap a square to place it. Tap a placed piece to turn it ↻.'
                    }
                  </p>`
            }
            <div class="bar">
              <button
                class="btn"
                aria-label="Undo"
                ?disabled=${this.walk || !this.undoStack.length}
                @click=${this.undo}
              >
                ↶
              </button>
              <button class="btn primary run" @click=${this.runIt}>Run</button>
              ${
                this.hints < l.hints.length
                  ? html`<button class="btn" ?disabled=${this.walk} @click=${() => this.hints++}>
                      Hint
                    </button>`
                  : nothing
              }
              <details class="more" ?hidden=${this.walk}>
                <summary class="btn" aria-label="More">⋯</summary>
                <div class="panel more-body">
                  <p class="row">
                    <button class="btn" @click=${this.reset}>Reset</button>
                    <button
                      class="btn"
                      aria-pressed=${this.tool === 'remove'}
                      @click=${this.toggleRemove}
                    >
                      Remove
                    </button>
                    <button class="btn" @click=${this.startWalk}>How to play</button>
                  </p>
                  <details>
                    <summary>How it works</summary>
                    <ul class="small">
                      <li>■ Power and switched-on levers power the squares next to them.</li>
                      <li>Wires that touch are one wire: all on or all off.</li>
                      <li>Repeater: passes power the way its arrow points.</li>
                      <li>Torch: on when nothing powers it from behind.</li>
                      <li>Both: on when both sides are powered.</li>
                      <li>Either: on when one side is powered.</li>
                      <li>Each repeater, torch, both or either adds one step of delay.</li>
                      <li>If the power keeps flickering, it can't win: make it settle.</li>
                    </ul>
                  </details>
                </div>
              </details>
            </div>
          </div>
        </div>
        <div class="board-col">
          <div class="board-box">
            <div
              class="board"
              role="grid"
              aria-label="Puzzle board"
              style="grid-template-columns: repeat(${l.width}, auto); --cell: ${this.cell}px"
            >
              ${Array.from({ length: l.height }, (_, y) =>
                Array.from({ length: l.width }, (_, x) => {
                  const f = l.fixed[`${x},${y}`];
                  const placed = this.board.placements.find((p) => p.x === x && p.y === y);
                  const mi = this.marks.findIndex((m) => m.x === x && m.y === y);
                  const mark = this.marks[mi];
                  return html`<button
                    class="cell ${f?.kind === 'block' ? 'block' : ''} ${mark ? 'marked' : ''}"
                    role="gridcell"
                    aria-label=${this.cellLabel(x, y, f, placed) + (mark ? `. ${markText(mark)}` : '')}
                    @click=${() => this.tap(x, y)}
                  >
                    ${this.cellSvg(x, y, f, placed)}
                    ${
                      mi === 0 && this.sheetOpen
                        ? html`<span
                            class="callout ${y >= l.height / 2 ? 'up' : ''} ${x >= l.width / 2 ? 'end' : ''}"
                            aria-hidden="true"
                            >${markText(mark!)}</span
                          >`
                        : nothing
                    }
                  </button>`;
                }),
              )}
            </div>
          </div>
          <p class="small muted">${key.join(' · ')}</p>
        </div>
      </div>`;
  }

  private sheet(l: RedstoneLevel, r: RunResult, next: RedstoneLevel | undefined) {
    const hint =
      this.hints < l.hints.length
        ? html`<button class="btn" @click=${() => this.hints++}>Hint</button>`
        : nothing;
    if (r.outcome !== 'win') {
      const k = this.marks.length;
      return html`<section class="panel sheet" role="status">
        <h2 tabindex="-1">Not quite</h2>
        <p>
          ${
            r.outcome === 'flicker'
              ? 'The power keeps switching on and off. Look for a loop.'
              : `${k} ${k === 1 ? "lamp isn't" : "lamps aren't"} right yet. Look at the marked lamp.`
          }
        </p>
        <p class="row">
          <button class="btn green" @click=${this.closeSheet}>Fix it</button>
          ${hint}
          <button class="btn" @click=${this.watch}>Watch again</button>
        </p>
      </section>`;
    }
    const medal = medalOf(l, r);
    const goldLamps = Object.values(l.fixed).filter((f) => f.kind === 'lamp' && f.goal).length;
    const rewards = (this.server.newRewards ?? []).map(rewardById).filter(Boolean);
    return html`<section class="panel sheet" role="status">
      <h2 tabindex="-1">${medal === 'gold' ? '★ Gold medal!' : 'Solved!'}</h2>
      ${medal !== 'gold' ? html`<p>Use ${l.budget} pieces or fewer for gold.</p>` : nothing}
      <p>You lit the ${goldLamps > 1 ? 'lamps' : 'lamp'} with ${r.piecesUsed} pieces.</p>
      ${this.server.state === 'saving' ? html`<p class="small muted">Saving…</p>` : nothing}
      ${this.server.state === 'error' ? html`<p class="small">${this.server.message} <button class="btn" @click=${this.runIt}>Try again</button></p>` : nothing}
      ${
        rewards.length
          ? html`<h3>New for your character</h3>
              <p class="row">
                ${rewards.map((x) => html`<span class="row">${itemIcon(x!)} <strong>${x!.name}</strong></span>`)}
              </p>
              <p><a class="btn" href="/fun/character">Equip it</a></p>`
          : nothing
      }
      <p class="row">
        ${next ? html`<button class="btn green" @click=${() => navigate(`/fun/redstone?level=${next.id}`)}>Next puzzle</button>` : nothing}
        <button class="btn" @click=${this.closeSheet}>Play again</button>
      </p>
    </section>`;
  }

  // ---------- walkthrough ----------

  private get coach() {
    return this.renderRoot.querySelector<KaWalkthrough>('ka-walkthrough');
  }

  /** Runs the walkthrough on level 1 on a fresh board; the kid's own level-1 draft is kept. */
  private startWalk = () => {
    markWalkthroughSeen('redstone');
    this.closeMenu();
    if (this.levelId === WALK_LEVEL) return this.beginWalk();
    this.walkNext = true;
    navigate(`/fun/redstone?level=${WALK_LEVEL}`);
  };

  private beginWalk() {
    this.clearResult();
    this.board = { placements: [], levers: {} };
    this.undoStack = [];
    this.hints = 0;
    this.tool = 'wire';
    this.walkStep = null;
    this.walk = true;
  }

  private onWalkEnd() {
    this.walk = false;
    this.walkStep = null;
    const l = this.level!;
    const kept = loadDraft(l);
    // The kid's own board comes back; with none, the walkthrough's board becomes the draft.
    if (kept.placements.length) {
      this.board = kept;
      this.undoStack = [];
      this.clearResult();
    } else saveDraft(l, this.board);
  }

  override render() {
    if (!currentKid()) return invitation();
    const l = this.level;
    return html`${l ? this.play(l) : this.levelList()}
      <ka-walkthrough
        .steps=${this.steps}
        ?open=${this.walk}
        @ka-walkthrough-step=${(e: CustomEvent<{ id: string }>) => (this.walkStep = e.detail.id)}
        @ka-walkthrough-end=${this.onWalkEnd}
      ></ka-walkthrough>`;
  }
}
customElements.define('ka-redstone', KaRedstone);
